// taken from https://raw.githubusercontent.com/donmccurdy/expression-eval/main/index.ts
// -------------------
// THIS FILE SHOULD BE ALMOST EQUIVALENT TO expression-eval.ts (SAME FOLDER)
// IF YOU CHANGE SOMETHING HERE, MAKE SURE TO ADJUST THE OTHER FILE AS WELL
// -------------------

import jsep, { CoreExpression } from 'jsep';
import { Visitor } from './ExpressionVisitor';
const binops: Record<string, (a: any, b: any) => any> = {
  '||': function (a: any, b: any) {
    return a || b;
  },
  '&&': function (a: any, b: any) {
    return a && b;
  },
  '|': function (a: any, b: any) {
    return a | b;
  },
  '^': function (a: any, b: any) {
    return a ^ b;
  },
  '&': function (a: any, b: any) {
    return a & b;
  },
  '==': function (a: any, b: any) {
    return a == b;
  }, // jshint ignore:line
  '!=': function (a: any, b: any) {
    return a != b;
  }, // jshint ignore:line
  '===': function (a: any, b: any) {
    return a === b;
  },
  '!==': function (a: any, b: any) {
    return a !== b;
  },
  '<': function (a: any, b: any) {
    return a < b;
  },
  '>': function (a: any, b: any) {
    return a > b;
  },
  '<=': function (a: any, b: any) {
    return a <= b;
  },
  '>=': function (a: any, b: any) {
    return a >= b;
  },
  '<<': function (a: any, b: any) {
    return a << b;
  },
  '>>': function (a: any, b: any) {
    return a >> b;
  },
  '>>>': function (a: any, b: any) {
    return a >>> b;
  },
  '+': function (a: any, b: any) {
    return a + b;
  },
  '-': function (a: any, b: any) {
    return a - b;
  },
  '*': function (a: any, b: any) {
    return a * b;
  },
  '/': function (a: any, b: any) {
    return a / b;
  },
  '%': function (a: any, b: any) {
    return a % b;
  },
};

const unops: Record<string, (a: any) => any> = {
  '-': function (a: any) {
    return -a;
  },
  '+': function (a: any) {
    return +a;
  },
  '~': function (a: any) {
    return ~a;
  },
  '!': function (a: any) {
    return !a;
  },
};

declare type operand = number | string;
declare type unaryCallback = (a: operand) => operand;
declare type binaryCallback = (a: operand, b: operand) => operand;

function evaluateArray(list: jsep.Expression[], context: Visitor) {
  return list.map(function (v) {
    return evaluate(toCoreExpression(v), context);
  });
}
function evaluateMember(node: jsep.MemberExpression, context: Visitor) {
  const object = evaluate(toCoreExpression(node.object), context);
  let key: string;
  if (node.computed) {
    key = evaluate(toCoreExpression(node.property), context);
  } else {
    key = (node.property as jsep.Identifier).name;
  }
  if (/^__proto__|prototype|constructor$/.test(key)) {
    throw Error(`Access to member "${key}" disallowed.`);
  }
  if (Array.isArray(object)) {
    /*
    If we are calling MemberExpression on Array, let's call it on first element instead

    The use case: if there's a Custom Field of Dictionary type, it could contain several values.
    Configuration regarding single vs multiple value is done within UI editor, so on Model level we always store Dictionary fields as arrays.
    So, e.g. for RefDoctor in Case field, we receive the following data:
    {
      case: { 'Additional doctor': [ {id: 1, value: 'DOC1', values: {Id: 1, ShortName: 'blablabla', Title: 'zxc', Name: 'DOC1'} } ]
    }
     */
    return [object, {}];
  } else {
    const parentPath = object?.path ?? '?';
    context.getMember(parentPath, key);
    return [object, { path: `${parentPath}["${key}"]` }];
  }
}
type BinopsKey = keyof typeof binops;
type UnopsKey = keyof typeof unops;

export function visitExpression(expression: string, visitor: Visitor) {
  const parsedExpression = jsep(expression);

  evaluate(toCoreExpression(parsedExpression), visitor);
}

function evaluate(node: CoreExpression, context: Visitor): any {
  switch (node.type) {
    case 'ArrayExpression':
      return evaluateArray(node.elements, context);

    case 'BinaryExpression':
      return binops[node.operator as BinopsKey](
        evaluate(toCoreExpression(node.left), context),
        evaluate(toCoreExpression(node.right), context)
      );

    case 'CallExpression':
      let fn, assign;
      if (node.callee.type === 'MemberExpression') {
        assign = evaluateMember(node.callee as jsep.MemberExpression, context);
        fn = assign[1];
      } else {
        fn = evaluate(toCoreExpression(node.callee), context);
      }
      // if (typeof fn !== 'function') {
      //   return undefined;
      // }

      const args = evaluateArray(node.arguments, context);
      context.callFunction(fn?.path, args);
      return { path: `${fn?.path}(${args.join(', ')})` };

    case 'ConditionalExpression':
      return evaluate(toCoreExpression(node.test), context)
        ? evaluate(toCoreExpression(node.consequent), context)
        : evaluate(toCoreExpression(node.alternate), context);

    case 'Identifier':
      context.getMember('', node.name);
      return {
        path: node.name,
      };

    case 'Literal':
      return node.value;

    case 'MemberExpression':
      return evaluateMember(node, context)[1];

    case 'ThisExpression':
      return context;

    case 'Compound':
      return context;

    case 'UnaryExpression':
      return unops[node.operator as UnopsKey](
        evaluate(toCoreExpression(node.argument), context)
      );

    default:
      return;
  }
}

function toCoreExpression(expression: jsep.Expression) {
  return expression as CoreExpression;
}
